import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useConfig } from "../../../contexts/ConfigContext";
import DataItemCard from "../data-grid/DataItemCard";
import { getSafeThumbnailUrl } from "../../../utilities/mediaUtils";
import { useUrlParamSelector } from "../../../store/urlParams";
import { getEntityColor } from "../../../utilities/dataItemUtils";
import type { ApiEndpoint, FlexibleDataItem, RelatedEntityItem } from "../../../types";

type RelationshipGroupViewProps = {
  relatedItemsData: RelatedEntityItem[];
  selectedCategoryFilter?: string;
  onSelect: (entity: FlexibleDataItem) => void;
  compact?: boolean;
};

type EnhancedRelatedItem = FlexibleDataItem & {
  properties?: {
    relatedEndpoint?: ApiEndpoint;
    relatedCategory?: string;
    endpointColor?: string;
    [key: string]: any;
  };
};

export default function RelationshipGroupView({
  relatedItemsData = [],
  selectedCategoryFilter = "all",
  onSelect,
  compact = false
}: RelationshipGroupViewProps) {
  const config = useConfig();
  const { selectedApiEndpoint } = useUrlParamSelector();

  // Convert related items to FlexibleDataItem format with enhanced type detection and filtering
  const convertedRelatedItems = useMemo<EnhancedRelatedItem[]>(() => {
    if (!relatedItemsData.length) {
      return [];
    }

    // Parse relationship description to determine direct vs indirect (from RelatedItemsRing logic)
    const parseRelDescription = (desc: string | undefined): { relationship_type?: "direct" | "indirect" | "default_to_direct" } => {
      if (!desc) return { relationship_type: "default_to_direct" };
      try {
        const parsed = JSON.parse(desc);
        if (parsed.relationship_type === "indirect") {
          return { relationship_type: "indirect" };
        }
        return { relationship_type: "default_to_direct" };
      } catch (e) {
        return { relationship_type: "default_to_direct" };
      }
    };

    // Map each related item to include endpoint information and enhanced type data
    const itemsWithEndpoints = relatedItemsData.map((item) => {
      const category = (item as any).category || "unknown";
      const relationshipType = (item as any).relationshipType || "unknown";

      // Preserve media metadata from API rows (embed/image/video) so thumbnail
      // resolution can use backend-provided URLs without lossy reconstruction.
      let remoteDetails: any = (item as any).remote_details;
      if (typeof remoteDetails === "string") {
        try {
          remoteDetails = JSON.parse(remoteDetails);
        } catch {
          // Keep original value when it is not valid JSON
        }
      }
      const apiThumbnail = (item as any).entity_thumbnail || (item as any).thumbnail || remoteDetails?.thumbnail || undefined;

      // Determine entity type based on relationship structure (from RelatedItemsRing logic)
      let entityType: "geo" | "related" | "media" = "related";
      if (relationshipType === "media") {
        entityType = "media";
      } else if (relationshipType === "geo_entities") {
        entityType = "geo";
      } else if (relationshipType === "related_entities") {
        entityType = "related";
      }

      // Parse relationship description for direct/indirect classification
      const parsedDesc = parseRelDescription(item.rel_description);
      const isDirect = parsedDesc.relationship_type !== "indirect";

      // Find matching endpoint by category (V2: tableName; fallback: name, relationshipKey)
      const matchingEndpoint = config.apiEndpoints.find(
        (ep) => ep.tableName === category || ep.name.toLowerCase() === category.toLowerCase() || ep.relationshipKey === category
      );

      return {
        ...(item as any),
        id: item.id,
        // CRITICAL: Use item.name (not entity_name) based on actual item structure
        name: (item as any).name || item.entity_name || "Unknown",
        description: item.entity_description || "",
        thumbnail: apiThumbnail,
        url: (item as any).url,
        media_type: (item as any).media_type || (item as any).type,
        embed_url: (item as any).embed_url || remoteDetails?.embed_url,
        remote_details: remoteDetails,
        // CRITICAL: Use the matching endpoint's tableName as entityType for correct API routing
        entityType: matchingEndpoint?.tableName || (item as any).entityType || "related",
        geometry: "",
        properties: {
          ...item,
          rel_description: item.rel_description,
          is_primary: item.is_primary,
          // Add endpoint information for sorting and coloring
          relatedEndpoint: matchingEndpoint,
          relatedCategory: category,
          endpointColor: matchingEndpoint ? getEntityColor(matchingEndpoint.tableName as any) : getEntityColor(entityType),
          // CRITICAL: Store the correct endpoint for this related item
          correctEndpoint: matchingEndpoint,
          // Enhanced type information from RelatedItemsRing logic
          relationshipType: relationshipType,
          entityTypeClassification: entityType, // 'geo' | 'related' | 'media'
          isDirect: isDirect,
          isIndirect: !isDirect,
          // Type-specific styling
          typeColor: entityType === "media" ? "#ff6b6b" : entityType === "geo" ? "#4ecdc4" : "#45b7d1", // Default for 'related'
          typeBadge: entityType === "media" ? "📷" : entityType === "geo" ? "📍" : "🔗", // Default for 'related'
          directBadge: isDirect ? "🔗" : "🔄" // Direct vs indirect indicator
        }
      };
    });

    // Apply category and directness filtering (workshop branch approach)
    let filteredItems = itemsWithEndpoints;

    // Filter by category/endpoint
    if (selectedCategoryFilter !== "all") {
      filteredItems = filteredItems.filter((item) => item.properties?.relatedCategory === selectedCategoryFilter);
    }

    // Note: Directness filtering removed - not using indirect relationships anymore

    // Sort by endpoint/category first, then by item name (workshop branch approach)
    return filteredItems.sort((a, b) => {
      // Primary sort: endpoint name (this groups items by their data source)
      const endpointA = a.properties?.relatedEndpoint?.name || "Unknown";
      const endpointB = b.properties?.relatedEndpoint?.name || "Unknown";
      if (endpointA !== endpointB) {
        return endpointA.localeCompare(endpointB);
      }

      // Secondary sort: item name
      return a.name.localeCompare(b.name);
    });
  }, [relatedItemsData, config.apiEndpoints, selectedCategoryFilter]);

  // Group items by endpoint for row-based display
  const groupedByEndpoint = useMemo(() => {
    const groups: Record<string, FlexibleDataItem[]> = {};

    convertedRelatedItems.forEach((item) => {
      const category = item.properties?.relatedCategory || "unknown";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });

    return groups;
  }, [convertedRelatedItems, config.apiEndpoints]);

  return (
    <Box sx={{ p: 2, display: "grid", gap: "0.5rem", gridTemplateColumns: "1fr" }}>
      {Object.entries(groupedByEndpoint)
        .filter(([category]) => {
          // Apply category filter
          if (selectedCategoryFilter !== "all" && category !== selectedCategoryFilter) {
            return false;
          }
          return true;
        })
        .map(([category, items]) => {
          // Find endpoint info for this category
          const matchingEndpoint = config.apiEndpoints.find(
            (ep) => ep.tableName === category || ep.name.toLowerCase() === category.toLowerCase()
          );

          const displayName = matchingEndpoint?.name || category.toUpperCase();
          // const endpointColor = matchingEndpoint ? getEntityColor(matchingEndpoint.entityType!) : getEntityColor("related");

          // Use all items since we're not filtering by directness anymore
          const filteredGroupItems = items;

          if (filteredGroupItems.length === 0) return null;

          return (
            <Box key={category} sx={{ mb: 1.5 }}>
              {/* Reduced margin */}
              {/* Compact Row Layout with Left Column Header */}
              <Box
                sx={{
                  display: "flex",
                  gap: 16,
                  alignItems: "flex-start"
                }}
              >
                {/* Left Column: Vertical Endpoint Title */}
                <Box
                  sx={{
                    display: "flex",
                    minHeight: compact ? "7.5rem" : "10rem",
                    alignItems: "center",
                    justifyContent: "center",
                    p: "0.25rem 0.5rem",
                    borderRight: "1.5px solid",
                    borderRightColor: "grey.500"
                  }}
                >
                  {/* Vertical endpoint name */}
                  <Typography
                    variant='subtitle2'
                    sx={{
                      color: "#fff",
                      fontSize: "0.8rem",
                      writingMode: "vertical-rl",
                      textOrientation: "mixed",
                      transform: "rotate(180deg)", // Rotate so text reads bottom to top
                      whiteSpace: "nowrap",
                      letterSpacing: "1px"
                    }}
                  >
                    {displayName}
                  </Typography>
                </Box>

                {/* Right Column: Items in Horizontal Scroll */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: "hidden"
                  }}
                >
                  <Box
                    component='div'
                    sx={{
                      display: "flex",
                      overflowX: "auto",
                      overflowY: "hidden",
                      gap: "0.5rem"
                    }}
                    className='scroller'
                  >
                    {filteredGroupItems.map((item: any) => (
                      <Box
                        key={item.id}
                        sx={{
                          minWidth: compact ? "8.5rem" : "10rem",
                          width: compact ? "8.5rem" : "10rem",
                          height: compact ? "7.5rem" : "10rem",
                          flexShrink: 0
                        }}
                      >
                        {/*
                          Relationship media rows already include thumbnail URLs from the API.
                          Prefer those directly (especially embed media) before generic URL derivation.
                        */}
                        <DataItemCard
                          compact={true} // Always compact in grouped view
                          item={item}
                          imageUrl={
                            ((item as any)?.properties?.relationshipType === "media" &&
                              ((item as any).entity_thumbnail || (item as any).thumbnail || (item as any)?.remote_details?.thumbnail)) ||
                            getSafeThumbnailUrl(item, (item as any)?.properties?.correctEndpoint || selectedApiEndpoint)
                          }
                          onSelect={onSelect}
                          endpoint={(item as any)?.properties?.correctEndpoint || selectedApiEndpoint}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
    </Box>
  );
}
